import React, { useEffect, useState } from 'react';
import CardStoreList from './CardStoreList';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import ButtonPrimary from '../Form/ButtonPrimary';
import { TbCurrentLocation, TbMoodConfuzed, TbX, TbZoom } from 'react-icons/tb';
import PopoverCustom from '../Form/Popover';
import StoreService, { StoreListProps } from '../../Services/StoreService';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import { ImSpinner8 } from 'react-icons/im';
import { useLoginContext } from '../../Contexts/LoginContext';
import { IMaskInput } from 'react-imask';
import { useStoreContext } from '../../Contexts/StoreContext';
import { orderTypeAsSlug } from '../../Utils/Helpers';
import Confirm from './Confirm';
import Modal from './Modal';
import { useCartHook } from '../../Hooks/CartHook';
import { useCartContext } from '../../Contexts/CartContext';
import Img from './Img';

const StoreList = () => {

    const [stores, setStores] = useState<any>([]);
    const [storesFiltered, setStoresFiltered] = useState<any>([]);
    const [noStores, setNoStores] = useState(false);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const { orderType, getOrderTypeIdByName } = useOrderTypeContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconError, company, states, setStates } = useLayoutContext();
    const { setGeolocationIsBlocked } = useLoginContext();
    const { currentStore, setCurrentStore } = useStoreContext();
    const [currentState, setCurrentState] = useState<string | null>(null);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingStores, setLoadingStores] = useState(false);
    const [showConfirmChangeStore, setShowConfirmChangeStore] = useState(false);
    const [storeToSelect, setStoreToSelect] = useState<any>(null);
    const { currentCart } = useCartContext();
    const { removeAll } = useCartHook();

    const resetCartItems = (store: any) => {
        return currentStore && currentCart?.items?.length > 0 && currentStore.id !== store.id;
    }

    const handleClickStore = (store: any) => {
        if (resetCartItems(store)) {
            setShowConfirmChangeStore(true);
            setStoreToSelect(store);
        } else {
            changeStore(store);
        }
    }

    const changeStore = (store: any) => {
        if (store.slug) {
            setCurrentStore(store);
            setNoStores(false);
            setStoreToSelect(null);
            if (resetCartItems(store)) {
                const payload = {
                    order_type: Number(getOrderTypeIdByName(orderType))
                }
                removeAll(currentCart.cart_id, payload);
            }
            navigate(`/${orderTypeAsSlug(orderType)}/${store.slug}`);
        } else {
            setMainLoading(true);
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle('Esta loja não possui slug');
            setMainLoadingMessage('');
            setMainLoadingCloseButton(true);
        }
    }

    const handleGetLocation = () => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              loadStoresList({ latitude: latitude, longitude: longitude });
              setGeolocationIsBlocked(false)
            },
            (error) => {
                setGeolocationIsBlocked(true)
                setTimeout(() => {
                    setMainLoading(true);
                    setMainLoadingIcon(false);
                    setMainLoadingIconError(true);
                    setMainLoadingTitle('Permissão de localização negada');
                    setMainLoadingMessage('Para usar esta função é necessário permitir acesso a geolocalização no seu dispositivo');
                    setMainLoadingCloseButton(true);
                    handleUniqueState()
                }, 250);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        } else {
          console.error('Geolocalização não é suportada por este navegador.');
        }
    };

    const handleSearchStore = () => {
        const normalize = (text: string = "") => text.toLowerCase().replace(/\s+/g, " ").trim();
        const normalizedSearch = normalize(search);
        const filtered = stores.filter((store: any) => {
            const name = normalize(store.name);
            const address = normalize(store.address);
            const city = normalize(store.city);
            return (
                name.includes(normalizedSearch) ||
                address.includes(normalizedSearch) ||
                city.includes(normalizedSearch)
            );
        });
        setStoresFiltered(filtered);
    }

    useEffect(() => {
        handleSearchStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    useEffect(() => {
        const loadStates = async () => {
            setLoadingStates(true);
            try {
                const storeService = new StoreService(orderType);
                const states = await storeService.states();
                setStates(states);
            } catch (error) {
                setStates([]);
            } finally {
                setLoadingStates(false);
            }
        }
        if (states.length === 0) {
            loadStates();
        }

        //* Inicializando pela geolocalização
        handleGetLocation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClickState = (state: string) => {
        if (state) {
            loadStoresList({ state: state });
        }
    }

    const handleUniqueState = () => {
        if (states.length > 0) {
            handleClickState(states[0]);
            setCurrentState(states[0]);
        }
    }

    const loadStoresList = async (params: StoreListProps) => {
        setLoadingStores(true);
        setNoStores(false);
        try {
            const storeService = new StoreService(orderType);
            const payload = {
                state: params.state?.toLowerCase(),
                latitude: params.latitude,
                longitude: params.longitude,
            }
            const stores = await storeService.stores(payload);
            setStores(stores);
            setStoresFiltered(stores);
            if(search.length > 0) {
                handleSearchStore();
            }
        } catch (error) {
            setNoStores(true);
            setStores([]);
        } finally {
            setLoadingStores(false);
        }
    }

    const handleClearState = () => {
        setStores([]);
        setStoresFiltered([]);
        setNoStores(false);
        setSearch('');
    }

    return (
        <>
            <div className='flex flex-col gap-6 h-full w-full'>

                <section className='flex flex-col gap-6 w-full items-center justify-center'>

                    {(!currentStore && company) && (
                        <div className='text-center w-full'>
                            <Img src={company.vector_image ?? company.image} alt="" className='inline-flex mt-4 transition-width ease-in-out duration-200 w-full max-w-40 md:max-w-48 md:max-h-max' />
                        </div>
                    )}

                    <div className='grid justify-center gap-1'>
                        <h3 className='text-center text-xl font-extrabold text-custom-primary mt-2 md:mt-0'>Faça seu pedido</h3>
                        <p className='text-base text-center font-medium leading-none'>Use sua localização ou selecione um estado para encontrar a unidade mais próxima.</p>
                    </div>
                    <div className='flex flex-col md:flex-row gap-4 w-full md:max-w-3xl'>
                        <div className='flex gap-1 w-full'>
                            <PopoverCustom outline placeholder='Estado' current={currentState} disabled={loadingStates || states.length === 0} loading={loadingStates} onClick={(e: string) => handleClickState(e)} onClear={() => handleClearState()} list={states} className='min-h-full w-auto rounded-lg rounded-r-none -mr-0.5' />
                            <div className="relative w-full">
                                <TbZoom size={20} className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-gray-300 z-10" aria-hidden="true"/>
                                <div className='relative flex items-center justify-between w-full'>
                                    <IMaskInput
                                        id="first_name"
                                        mask={/^[a-zA-ZÀ-ÿ0-9\s]*$/}
                                        radix=""
                                        value={search}
                                        onAccept={(value: string) => setSearch(value)}
                                        unmask={true} // true|false|'typed'
                                        placeholder={loadingStores ? 'Aguarde...' : 'Encontre a loja'}
                                        disabled={loadingStores || (stores && stores.length === 0)}
                                        className='h-12 w-full ring-2 ring-gray-200 rounded-lg rounded-l-none bg-transparent pl-10 font-bold placeholder:font-normal text-custom-primary placeholder:text-gray-400 placeholder:text-sm focus:ring-custom-primary disabled:bg-gray-100 transition-all duration-200 duration-250'
                                    />
                                    {search.length > 0 && <TbX size={22} onClick={() => setSearch('')} className='absolute cursor-pointer p-1 right-2 hover:bg-gray-200 rounded-full transition-all duration-200' />}
                                </div>
                            </div>
                        </div>

                        <div className='flex w-full md:w-auto justify-center'>
                            <ButtonPrimary onClick={() => handleGetLocation()} className='w-full lg:w-auto items-center justify-center gap-1 text-nowrap'>
                                <span><TbCurrentLocation size={20} /></span>
                                <span>Usar minha localização</span>
                            </ButtonPrimary>
                        </div>
                    </div>
                </section>

                {(!loadingStores && storesFiltered.length > 0) && (
                    <div className='flex gap-4 w-full items-center justify-center opacity-50'>
                        <hr className='w-full max-w-sm' />
                        <div className='flex gap-1 items-center justify-center text-nowrap text-xs'>
                            <span>{storesFiltered.length}</span>
                            <span>{storesFiltered.length > 1 ? 'lojas encontradas' : 'loja encontrada'}</span>
                        </div>
                        <hr className='w-full max-w-sm' />
                    </div>
                )}

                {loadingStores && (
                    <div className='flex gap-1 justify-center items-center text-sm'>
                        <ImSpinner8 className="animate-spin" />
                        Carregando lojas...
                    </div>
                )}

                {(!loadingStores && (noStores || storesFiltered.length === 0)) && (
                    <div className='flex gap-1 justify-center items-center text-sm'>
                        <TbMoodConfuzed />
                        Nenhuma loja encontrada {(search && search.length > 0) ? `com a sua pesquisa: "${search}"` : ''}
                    </div>
                )}

                {(!loadingStores && stores) && (
                    <section className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 xl:gap-5 transition-width ease-in-out duration-200'>
                    {storesFiltered.map((store: any, index: number) =>
                        <CardStoreList onClick={() => handleClickStore(store)} store={store} key={`${store.id}`} />
                    )}
                    </section>
                )}
            </div>

            {(currentStore && storeToSelect) && 
                <Modal open={showConfirmChangeStore} className="max-w-sm m-4" onClose={() => setShowConfirmChangeStore(false)}>
                    <Confirm
                        title='Confirma troca de loja?'
                        message={`Você atualmente está navegando na loja <strong>${currentStore.name}</strong> e quer trocar para <strong>${storeToSelect.name}</strong>. Se confirmar a troca, seu carrinho será zerado. Deseja continunar?`}
                        labelConfirm='Sim, quero trocar'
                        onConfirm={() => {
                            changeStore(storeToSelect);
                        }}
                        onCancel={() => {
                            setShowConfirmChangeStore(false); 
                        }}
                    />
                </Modal>
            }
        </>
    );
}

export default StoreList;
